import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';

/**
 * Module 7 — unread alerts hook.
 *
 * Returns { count, loading, refresh } for the current agent. Polls on mount
 * + supabase realtime subscription on alert_events insert/update so the
 * badge updates live when new alerts arrive (post-ingestion recompute) or
 * when the agent opens the alerts page on another tab.
 *
 * RLS already filters to agent_id = auth.uid(); we still pass the userId
 * to the count query so a missing session yields 0 instead of erroring.
 */
const useUnreadAlerts = (userId) => {
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!userId) {
      setCount(0);
      return;
    }
    setLoading(true);
    const { count: c, error } = await supabase
      .from('alert_events')
      .select('id', { count: 'exact', head: true })
      .eq('agent_id', userId)
      .is('read_at', null);
    if (!error) setCount(c ?? 0);
    setLoading(false);
  }, [userId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Live updates via supabase realtime (postgres_changes). RLS scopes the
  // channel to this agent's rows. We refresh on any change rather than
  // mutating the count locally to keep the hook simple.
  //
  // Defensive: WebSocket can fail (CSP blocking wss://, network captive portal,
  // browser policy on insecure context). Wrap in try/catch + a 60s polling
  // fallback so the bell badge stays usable even when realtime is unavailable.
  useEffect(() => {
    if (!userId) return undefined;
    let channel = null;
    try {
      channel = supabase
        .channel(`alert_events-unread-${userId}`)
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'alert_events', filter: `agent_id=eq.${userId}` },
          () => refresh(),
        )
        .subscribe((status, err) => {
          // status: 'SUBSCRIBED' | 'CHANNEL_ERROR' | 'TIMED_OUT' | 'CLOSED'
          if (status === 'CHANNEL_ERROR' && err) {
            // eslint-disable-next-line no-console
            console.warn('[useUnreadAlerts] realtime channel error (falling back to poll):', err.message ?? err);
          }
        });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[useUnreadAlerts] realtime subscribe threw (falling back to poll):', e?.message ?? e);
    }
    // 60s polling fallback — runs alongside realtime so it stays accurate
    // even when WebSocket dies mid-session.
    const pollId = setInterval(refresh, 60_000);
    return () => {
      clearInterval(pollId);
      if (channel) {
        try { supabase.removeChannel(channel); } catch { /* ignore */ }
      }
    };
  }, [userId, refresh]);

  return { count, loading, refresh };
};

export default useUnreadAlerts;
